import { brandCanvasTokens } from './brandCanvas'
import { type RampTheme, nudgeLightness } from './brandContrast'
import { contrastRatio } from './brandPalette'
import { brandSecondaryTokens } from './brandSecondary'
import { type Oklch, hexToOklch, oklchToHex } from './oklch'

export type BrandRampTokens = Record<string, string>

export interface BrandRamp {
  light: BrandRampTokens
  dark: BrandRampTokens
}

interface RungTemplate {
  key: string
  light: Oklch
  dark: Oklch
}

interface ThemedToken {
  name: string
  light: string
  dark: string
}

interface RgbaToken extends ThemedToken {
  prefix: string
}

interface OnPrimaryChoice {
  onPrimary: string
  rung500: string
  rung600: string
  score: number
}

const COBALT_LIGHT_ANCHOR: Oklch = { l: 0.475831, c: 0.252129, h: 272.5291 }
const COBALT_DARK_ANCHOR: Oklch = { l: 0.593937, c: 0.207629, h: 279.5549 }

const COBALT_RAMP: readonly RungTemplate[] = [
  {
    key: '50',
    light: { l: 0.953806, c: 0.021396, h: 285.9871 },
    dark: { l: 0.223047, c: 0.068107, h: 278.1371 },
  },
  {
    key: '100',
    light: { l: 0.912128, c: 0.040772, h: 284.0427 },
    dark: { l: 0.272902, c: 0.10083, h: 275.9035 },
  },
  {
    key: '200',
    light: { l: 0.837504, c: 0.076715, h: 282.9788 },
    dark: { l: 0.345087, c: 0.144415, h: 274.3527 },
  },
  {
    key: '300',
    light: { l: 0.728126, c: 0.130915, h: 281.6744 },
    dark: { l: 0.410636, c: 0.2009, h: 274.0066 },
  },
  {
    key: '400',
    light: { l: 0.593937, c: 0.207629, h: 279.5549 },
    dark: { l: 0.539333, c: 0.231398, h: 276.6774 },
  },
  { key: '500', light: COBALT_LIGHT_ANCHOR, dark: COBALT_DARK_ANCHOR },
  {
    key: '600',
    light: { l: 0.418024, c: 0.240951, h: 270.8658 },
    dark: { l: 0.728126, c: 0.130915, h: 281.6744 },
  },
  {
    key: '700',
    light: { l: 0.379802, c: 0.212569, h: 271.6743 },
    dark: { l: 0.837504, c: 0.076715, h: 282.9788 },
  },
  {
    key: '800',
    light: { l: 0.335308, c: 0.176836, h: 273.2344 },
    dark: { l: 0.912128, c: 0.040772, h: 284.0427 },
  },
  {
    key: '900',
    light: { l: 0.301497, c: 0.139965, h: 275.519 },
    dark: { l: 0.953806, c: 0.021396, h: 285.9871 },
  },
]

const RULE_A_TOKENS: readonly ThemedToken[] = [
  { name: '--ai-button-from', light: '#2620b0', dark: '#2a23cc' },
  { name: '--ai-button-mid', light: '#3a35e6', dark: '#4b45ee' },
  { name: '--ai-button-to', light: '#6b66f5', dark: '#6b66f5' },
  { name: '--chat-bubble-user', light: '#3a35e6', dark: '#6b66f5' },
  { name: '--voice-wave-vivid-from', light: '#c2c4fb', dark: '#c2c4fb' },
]

const RULE_B_TOKENS: readonly ThemedToken[] = [
  { name: '--color-primary-dark', light: '#141633', dark: '#e8ebf1' },
  { name: '--color-primary-dark-hover', light: '#1f2350', dark: '#fafbfc' },
  { name: '--color-primary-dark-text', light: '#f7f8fb', dark: '#0a0c12' },
]

const ALPHA_HEX_TOKENS: readonly ThemedToken[] = [
  { name: '--color-primary-faint', light: '22', dark: '2e' },
]

const RGBA_TOKENS: readonly RgbaToken[] = [
  { name: '--color-primary-ring', prefix: '', light: '0.08', dark: '0.1' },
  { name: '--color-primary-glow', prefix: '', light: '0.25', dark: '0.3' },
  { name: '--color-primary-glow-strong', prefix: '', light: '0.35', dark: '0.4' },
  { name: '--color-ai-glow', prefix: '', light: '0.35', dark: '0.35' },
  { name: '--ai-button-glow', prefix: '', light: '0.45', dark: '0.5' },
  { name: '--focus-ring', prefix: '0 0 0 3px ', light: '0.1', dark: '0.28' },
  { name: '--shadow-hover-primary', prefix: '0 4px 16px ', light: '0.3', dark: '0.3' },
]

const SURFACE: Record<RampTheme, string> = { light: '#ffffff', dark: '#12151d' }
const TEXT: Record<RampTheme, string> = { light: '#151a22', dark: '#e8ebf1' }
const ON_PRIMARY_LIGHT = '#ffffff'
const ON_PRIMARY_INK = '#0a0c12'
const TEXT_FLOOR = 4.5
const UI_FLOOR = 3
const DARK_ANCHOR_DELTA_L = COBALT_DARK_ANCHOR.l - COBALT_LIGHT_ANCHOR.l
const DARK_ANCHOR_RATIO_C = COBALT_DARK_ANCHOR.c / COBALT_LIGHT_ANCHOR.c
const DARK_ANCHOR_HEADROOM_DENOM = 1 - COBALT_LIGHT_ANCHOR.l
const ACHROMATIC_CHROMA = 0.02
const HEX_PATTERN = /^#[0-9a-fA-F]{6}$/

function forTheme<T extends { light: string; dark: string }>(entry: T, theme: RampTheme): string {
  return theme === 'light' ? entry.light : entry.dark
}

function darkAnchorL(brandL: number): number {
  return brandL + DARK_ANCHOR_DELTA_L * ((1 - brandL) / DARK_ANCHOR_HEADROOM_DENOM)
}

function ruleA(stock: Oklch, anchor: Oklch, stockAnchor: Oklch, hueShift: number): Oklch {
  const deltaL = stock.l - stockAnchor.l
  const headroom = deltaL > 0 ? (1 - anchor.l) / (1 - stockAnchor.l) : anchor.l / stockAnchor.l
  return {
    l: anchor.l + deltaL * headroom,
    c: anchor.c * (stock.c / stockAnchor.c),
    h: stock.h + hueShift,
  }
}

function attempt(
  onPrimary: string,
  raw500: Oklch,
  raw600: Oklch,
  theme: RampTheme
): OnPrimaryChoice {
  const rung500 = nudgeLightness(raw500, [
    { against: onPrimary, floor: TEXT_FLOOR },
    { against: SURFACE[theme], floor: UI_FLOOR },
  ])
  const rung600 = nudgeLightness(raw600, [{ against: onPrimary, floor: TEXT_FLOOR }])
  const score = Math.min(
    contrastRatio(onPrimary, rung500) / TEXT_FLOOR,
    contrastRatio(rung500, SURFACE[theme]) / UI_FLOOR,
    contrastRatio(onPrimary, rung600) / TEXT_FLOOR
  )
  return { onPrimary, rung500, rung600, score }
}

function resolveOnPrimary(raw500: Oklch, raw600: Oklch, theme: RampTheme): OnPrimaryChoice {
  const preview = oklchToHex(raw500)
  const preferred =
    contrastRatio(ON_PRIMARY_LIGHT, preview) >= contrastRatio(ON_PRIMARY_INK, preview)
      ? ON_PRIMARY_LIGHT
      : ON_PRIMARY_INK
  const first = attempt(preferred, raw500, raw600, theme)
  if (first.score >= 1) {
    return first
  }
  const other = preferred === ON_PRIMARY_LIGHT ? ON_PRIMARY_INK : ON_PRIMARY_LIGHT
  const fallback = attempt(other, raw500, raw600, theme)
  return fallback.score > first.score ? fallback : first
}

function rgbTriple(hex: string): string {
  const value = Number.parseInt(hex.slice(1, 7), 16)
  return `${(value >> 16) & 0xff}, ${(value >> 8) & 0xff}, ${value & 0xff}`
}

export function isBrandableColor(hex: string): boolean {
  if (!HEX_PATTERN.test(hex)) {
    return false
  }
  return hexToOklch(hex.toLowerCase()).c >= ACHROMATIC_CHROMA
}

function themeTokens(brand: Oklch, theme: RampTheme): BrandRampTokens {
  const stockAnchor = theme === 'light' ? COBALT_LIGHT_ANCHOR : COBALT_DARK_ANCHOR
  const anchor: Oklch =
    theme === 'light'
      ? { l: brand.l, c: brand.c, h: brand.h }
      : { l: darkAnchorL(brand.l), c: brand.c * DARK_ANCHOR_RATIO_C, h: brand.h }
  const hueShift = brand.h - COBALT_LIGHT_ANCHOR.h
  const raw = COBALT_RAMP.map((rung) => ({
    key: rung.key,
    value: ruleA(theme === 'light' ? rung.light : rung.dark, anchor, stockAnchor, hueShift),
  }))
  const rawOf = (key: string): Oklch => {
    const entry = raw.find((candidate) => candidate.key === key)
    if (entry === undefined) {
      throw new Error(`no template rung ${key}`)
    }
    return entry.value
  }
  const choice = resolveOnPrimary(rawOf('500'), rawOf('600'), theme)
  const tokens: BrandRampTokens = {}
  for (const entry of raw) {
    if (entry.key === '500') {
      tokens['--color-primary-500'] = choice.rung500
      continue
    }
    if (entry.key === '600') {
      tokens['--color-primary-600'] = choice.rung600
      continue
    }
    if (entry.key === '50' || entry.key === '100') {
      tokens[`--color-primary-${entry.key}`] = nudgeLightness(entry.value, [
        { against: TEXT[theme], floor: TEXT_FLOOR },
      ])
      continue
    }
    tokens[`--color-primary-${entry.key}`] = oklchToHex(entry.value)
  }
  tokens['--color-on-primary'] = choice.onPrimary
  const triple = rgbTriple(choice.rung500)
  for (const entry of ALPHA_HEX_TOKENS) {
    tokens[entry.name] = `${choice.rung500}${forTheme(entry, theme)}`
  }
  for (const entry of RGBA_TOKENS) {
    tokens[entry.name] = `${entry.prefix}rgba(${triple}, ${forTheme(entry, theme)})`
  }
  for (const entry of RULE_A_TOKENS) {
    const stock = hexToOklch(forTheme(entry, theme))
    tokens[entry.name] = oklchToHex(ruleA(stock, anchor, stockAnchor, hueShift))
  }
  for (const entry of RULE_B_TOKENS) {
    const stock = hexToOklch(forTheme(entry, theme))
    tokens[entry.name] = oklchToHex({ l: stock.l, c: stock.c, h: stock.h + hueShift })
  }
  return {
    ...tokens,
    ...brandCanvasTokens(brand, theme),
    ...brandSecondaryTokens(brand, theme, choice.rung500),
  }
}

export const PRIMARY_TOKEN_NAMES: readonly string[] = [
  '--color-primary-50',
  '--color-primary-100',
  '--color-primary-200',
  '--color-primary-300',
  '--color-primary-400',
  '--color-primary-500',
  '--color-primary-600',
  '--color-primary-700',
  '--color-primary-800',
  '--color-primary-900',
  '--color-primary-faint',
  '--color-primary-ring',
  '--color-primary-glow',
  '--color-primary-glow-strong',
  '--color-on-primary',
  '--color-primary-dark',
  '--color-primary-dark-hover',
  '--color-primary-dark-text',
  '--ai-button-from',
  '--ai-button-mid',
  '--ai-button-to',
  '--ai-button-glow',
  '--color-ai-glow',
  '--chat-bubble-user',
  '--voice-wave-vivid-from',
  '--focus-ring',
  '--shadow-hover-primary',
]

export function primaryTokensOnly(tokens: BrandRampTokens): BrandRampTokens {
  const picked: BrandRampTokens = {}
  for (const name of PRIMARY_TOKEN_NAMES) {
    const value = tokens[name]
    if (value !== undefined) {
      picked[name] = value
    }
  }
  return picked
}

export function brandRamp(hex: string): BrandRamp | null {
  if (!isBrandableColor(hex)) {
    return null
  }
  const brand = hexToOklch(hex.toLowerCase())
  return { light: themeTokens(brand, 'light'), dark: themeTokens(brand, 'dark') }
}
